import type { RouterLink, SearchHit } from './searchModels';
import type { LanguageCode } from './asyncOperationItemCreationTypes';
import type { ReprocessActionType, ArchiveFileTransferParams, CanceledStateFieldName } from './asyncOperationPrimitives';
import type { ItemMetaDataDto } from './fieldValue';
import type { MetadataDeltaPayload, MetadataSnapshot } from './mdfModels';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { Fraction } from '../util/fractionType';
import type { ExternalProxyDto } from './externalProxyDto';
import type { GeoShape } from './GeoLocation';
import type { AafLocationUpdatePayload } from './aafDto';
import type { RelationsEntity } from './relationsEntity';
import type { ItemVersionInfo } from './itemVersionInfo';
import type { ProxyFileLocation } from './fileLocation';
import { isInstanceOfFileLocation } from './fileLocationTypeGuards';
import type { DynamoItemType, SearchItemType } from './itemTypes';
import type { UsageRuleSearchDocument } from './timedMetadataModels';
import type { VirtualClipModel } from './virtualClipModel';
import type { ThumbnailInfo } from './thumbnailInfo';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import { isValidDate } from '../util/date';
import type { LivePreviewLocation } from './livePreviewLocation';
import type { Ingest, HighresDeletion, MediaLocationTypes, ArchiveInfo, GeneratedContentLocation } from './itemPropTypes';
import type { ExtendedItemDto } from './extendedItemDto';
import { possibleHighresDeletionStates } from './itemPropTypes';
import type { CompleteType } from '../util/general';
import type { StreamRoleId } from './mediaAvailability';

export const isInstanceOfIngestLocation = (obj: unknown): obj is Ingest => {
  if (!isObject(obj)) return false;
  const probablyIngestLocation = obj as Partial<Ingest>;
  return isInstanceOfFileLocation(probablyIngestLocation.source)
    && (!probablyIngestLocation.agent || (typeof probablyIngestLocation.agent === 'string'));
};

/**
 * Associated with highres deletion update actions via the item update API.
 */
export type HighresDeletionUpdateDto = Partial<Pick<HighresDeletion, 'deletionState' | 'statusMessage' | 'deletionDate'>>;

export type UpdateHighresDeletionStateDto = HighresDeletionUpdateDto & Partial<Pick<HighresDeletion, 'highresLocation'>>;

export function isInstanceOfHighresDeletionUpdateDto(dto: unknown): dto is HighresDeletionUpdateDto {
  if (!isObject(dto)) return false;
  const possibleDto = dto as Partial<HighresDeletionUpdateDto>;

  return (isUndefined(possibleDto.statusMessage) || isString(possibleDto.statusMessage))
    && (isUndefined(possibleDto.deletionState) || isString(possibleDto.deletionState) && possibleHighresDeletionStates.includes(possibleDto.deletionState))
    && (isUndefined(possibleDto.deletionDate) || isString(possibleDto.deletionDate) && isValidDate(possibleDto.deletionDate));
}

/**
 * Represents an item sent from the server to the client
 */
export interface SearchResultHit
 extends Pick<
   ExtendedItemDto,
   | 'id'
   | 'itemState'
   | 'isDeleting'
   | 'transcodingState'
   | 'createdOn'
   | 'archiveInfo'
   | 'progressInfo'
   | 'originalFileName'
   | 'itemType'
   | 'folderParents'
   | 'proxy'
   | 'indexUrl'
   | 'uploadProgressInfo'
   | 'archiveFileTransferState'
   | 'aaf'
   | 'livePreviewLocation'
   | 'media'
   | 'externalProxyInfo'
   | 'aafLocation'
   | 'ingest'
   | 'mediaType'
   | 'mediaFrameRate'
   | 'editProxyFormat'
   | 'modifiedOn'
   | 'isLive'
   | 'generatedContentLocation'
   | 'visibleTo'
   | 'updatableBy'
   | 'scheduledRecording'
   | 'transcriptionState'
   | 'transcriptionEnabled'
   | 'webProxyLocation'
   | 'technicalMetadata'
   | 'isP2Part'
   | 'webProxyAudioDownMixInfo'
   | 'customIcons'
 > {
 vttUrl?: string;
 itemType: SearchItemType;
 name?: string;
 description?: string;

 // Differs from item resource DTO, in that metadata may have highlights.
 // TODO / type warning: We only return a subset of the metadata formData
 // from the search API, but this is not clear from the type.
 // TODO: We don't return metadata for folders.
 metadata: ItemMetaDataDto;

/**
 * @deprecated Use "technical_media_duration" in item.technicalMetadata.formData instead.
 */
 mediaDuration?: number;
 thumbnail?: string;

 thumbnailStrip?: string;
 ingestSourceFullPath?: string;
 ingestSourceS3Bucket?: string;
 ingestSourceStorageId?: string;
 createdBy?: string; // Included for any other item.
 owner?: string; // Included when it's a folder.
 languageCode?: LanguageCode;
 operationsLink?: string;
 sourceFileLocation?: MediaLocationTypes;
 searchHits?: SearchHit[];
 isArchived?: boolean;
 geoLocation?: GeoShape;
 virtualClipInfo?: VirtualClipModel;
 warnings?: string[];
 hasProxy?: boolean;
 editProxyIngested?: boolean;
 hasEditProxy?: boolean;
 editProxyAvailable?: boolean;
 mediaSize?: number;
 proxySize?: number;
 parent?: string;
 mediaWidth?: number;
 mediaHeight?: number;

 // True if there exists one or more restricted material log entries for the item.
 hasRestrictedMaterial?: boolean;
 // Associated usage rules for all log entries
 usageRules?: UsageRuleSearchDocument[];
 // True if the item is selected in item list
 selected?: boolean;

 exiftags?: string;

 highresDeletion?: Pick<HighresDeletion, 'deletionState' | 'initiatedBy' | 'deletionDate'>;
 translationLanguage?: string;
 imageIds?: string[]; // From PersonDto
}

type SearchScrollApiResponseFolderType = { id: string, itemType: 'folder', name: string };
// We add 'name' here for not breaking backwards compatibility for getFolderContent api
// as we used to send 'name' instead of title both for item and folder types in the past
// the 'name' property is not and should not be returned in other apis e.g., search api
export type SearchScrollApiResponseDynamoItemType =  { id: string, itemType: DynamoItemType, title: string, name?: string };
export type SearchScrollApiResponseItemSlim = SearchScrollApiResponseFolderType | SearchScrollApiResponseDynamoItemType;

// This is a subset of 'fieldsToFetchForSearchItemHit' of properties
// from OpenSearch that can be directly returned to the client without any
// processing.
// Unlike fieldsToFetchForSearchItemHit, this list doesn't contain properties
// that are needed for deriving other properties such as thumbnailInfo (used for deriving thumbnail url) etc.
// FIXME: Keeping one list of fields that both define a DTO and is used as input to OpenSearch
// is sketchy.
export const fieldsToPickForFatScroll = <const>[
  'id',
  'originalFileName',
  'mediaDuration',
  'mediaSize',
  'proxySize',
  'folderParents',
  'ingestSourceS3Bucket',
  'ingestSourceFullPath',
  'itemState',
  'isDeleting',
  'itemType',
  'mediaType',
  'transcriptionState',
  'transcriptionEnabled',
  'transcodingState',
  'geoLocation',
  'virtualClipInfo',
  'archiveInfo',
  'hasProxy',
  'editProxyIngested',
  'hasEditProxy',
  'editProxyAvailable',
  'mediaFrameRate',
  'name',
  'createdOn',
  'visibleTo',
  'hasRestrictedMaterial',
  'usageRules',
  'createdBy',
  'owner',
  'progressInfo',
  'uploadProgressInfo',
  'warnings',
  'imageIds',
  'isArchived',
  'archiveFileTransferState',
  'media',
  'externalProxyInfo',
  'aafLocation',
  'ingest',
  'editProxyFormat',
  'modifiedOn',
  'isLive',
  'generatedContentLocation',
  'languageCode',
  'translationLanguage',
  'updatableBy',
  'scheduledRecording',
];

export type SearchScrollApiResponseItemFat = Pick<SearchResultHit, typeof fieldsToPickForFatScroll[number]> & {
  metadata?: {
    formData?: { default_title?: string }
  }
};

type SearchScrollResponseCommon = {
  mScrollId: string | undefined,
  total: number | undefined, // only returned on the first api call that starts to scroll
};

export type SearchScrollResponseSlim = {
  items: SearchScrollApiResponseItemSlim[];
} & SearchScrollResponseCommon;

export type SearchScrollResponseFat = {
  items: SearchScrollApiResponseItemFat[];
} & SearchScrollResponseCommon;

export type SearchScrollResponseFull = {
  items: SearchResultHit[];
  /**
   * This parameter is used for processing MDF fields with fat-version search.
   * Because processing metadata fields requires additional information that the `mScrollId` does not supply.
   */
  mdfProcessParams?: {
    searchString: string | undefined;
  };
} & SearchScrollResponseCommon;

export type SearchScrollResponse = SearchScrollResponseSlim | SearchScrollResponseFat | SearchScrollResponseFull;

export function isSearchScrollResponseType(obj: unknown): obj is SearchScrollResponse {
  if (!isObject(obj)) return false;
  const maybe = obj as Partial<SearchScrollResponse>;
  return !!maybe.items;
}

export type FolderSearchResultHit = SearchResultHit & {
  name: string;
  itemType: 'folder';
  owner: string;
};

export function isFolderSearchResultHit(hit: SearchResultHit): hit is FolderSearchResultHit {
  return 'name' in hit && hit.itemType === 'folder';
}

/**
 * Represents a SearchResultHit and some extra information
 * injected by the client in the search views.
 *
 * TODO: It's not clear what this type represents:
 * Contrary to the above comment, some of fields Picked here
 * from ExtendedItemDto etc are not returned by the search API
 * nor something the client can inject without doing a getItem.
 * We seem to often use this type when don't really know exactly
 * what we have, except that it is Item-ish.
 */
export type Item = SearchResultHit
  & Pick<ExtendedItemDto, 'technicalMetadata' | 'isExternalUpload' | 'focusPoint' | 'editProxyFormat' | 'liveRecording' | 'isLive' | 'editProxyUrl' | 'transcriptionEnabled' | 'transcriptionState' | 'isP2Part' | 'isHidden' | 'highRes' | 'indexUrl' | 'timecode' | 'scheduledRecording' | 'analyticPlugins' | 'hasRelations' | 'customIcons' | 'keldaIngest'>
  & Partial<Pick<ItemVersionInfo, 'versionCreatedOn' | 'versionState'>>
  & InjectedItemProps;

/**
 * When selecting a search result item (e.g., for opening quick preview),
 * we load some additional info that is not available on a plain search
 * result hit, but without fetching all the item fields needed for a LoadedItem.
 */
export type LoadedSearchItem = Item & Pick<LoadedItem,
  'metadataSnapshot' |
  'aaf' |
  'hasRelations' |
  'highRes' |
  'ingestSourceFullPath' |
  'ingestSourceS3Bucket' |
  'ingestSourceStorageId' |
  'metadata' |  // Search results do not come with the full metadata payload
  'metadataVersion' |
  'technicalMetadata' |
  'timecode' |
  'pagedProxy' |
  'exifTagsUrl' |
  'nonScheduledRecording' |
  'taskEntries'
>;

export function isLoadedSearchItem(item: Item): item is LoadedSearchItem {
  const maybe = item as Partial<LoadedSearchItem>;
  return 'hasRelations' in maybe && 'metadataVersion' in maybe && 'metadataSnapshot' in maybe;
}

/**
 * Represents the properties the client injects in the item objects.
 */
export interface InjectedItemProps {
  loading?: boolean;
  latestVersionId?: string;
  versionName?: string;
  routerLink?: RouterLink;
  selectedSearchHit?: SearchHit;
  typeIcon?: string;
  archiveInfo?: ArchiveInfo;

  // injected by Thumbnail.vue component, which represents the final thumbnail
  // source after having tried to load the thumbnail path.
  resolvedThumbnailUrl?: string;

  // In memory state for metadata conflict resolution strategy
  metadataSnapshot?: MetadataSnapshot;
}

export const clientInjectedItemProps: (keyof InjectedItemProps)[] = ['archiveInfo', 'resolvedThumbnailUrl', 'routerLink', 'selectedSearchHit', 'typeIcon'];

type UpdatePendingArchiveRequest = {
  transferParams: ArchiveFileTransferParams;
};

export function isInstanceOfUpdatePendingArchiveRequest(obj: unknown): obj is UpdatePendingArchiveRequest {
  if (!isObject(obj)) return false;
  const maybe = obj as Partial<UpdatePendingArchiveRequest>;
  return isObject(maybe.transferParams);
}

/**
 * Represents a fully loaded item from the server.
 */
export type LoadedItem = Item & Pick<ExtendedItemDto,
  | 'metadataVersion'
  | 'personDetectionErrorMessage'
  | 'transcriptionErrorMessage'
  | 'celebrityDetectionErrorMessage'
  | 'labelDetectionErrorMessage'
  | 'textDetectionErrorMessage'
  | 'transcodingErrorMessage'
  | 'itemState'
  | 'hasEditProxy'
  | 'editProxyUrl'
  | 'aaf'
  | 'highRes'
  | 'technicalMetadata'
  | 'uploadMethods'
  | 'archiveLocationFullPath'
  | 'archiveS3Bucket'
  | 'archiveFileTransferState'
  | 'archiveFileTransferErrorMessage'
  | 'transcriptionEnabled'
  | 'languageCode'
  | 'transcriptionService'
  | 'labelDetectionEnabled'
  | 'textDetectionEnabled'
  | 'personDetectionEnabled'
  | 'createUnknownPersons'
  | 'transcriptionChannels'
  | 'transcriptionChannelIdentification'
  | 'detectedPersons'
  | 'detectedCelebrities'
  | 'detectedLabels'
  | 'media'
  | 'ingest'
  | 'keldaIngest'
  | 'attachments'
  | 'mediaFrameRate'
  | 'editProxyFormat'
  | 'pagedProxy'
  | 'purgeDate'
  | 'liveRecording'
  | 'nonScheduledRecording'
  | 'modifiedOn'
  | 'timecode'
  | 'scheduledRecording'
  | 'conformDetailsIfRenderedSequence'
  | 'exportedSequenceDetailsUrl'
  | 'pendingSubclipOps'
  | 'textDetectionSettings'
  | 'analyticPlugins'
  | 'mediaLastUsedOn'
  | 'mediaUsage'
  | 'taskEntries'
  | 'isSubclip'
  | 'personDetectionState'
  | 'transcriptionState'
  | 'celebrityDetectionState'
  | 'labelDetectionState'
  | 'textDetectionState'
  | 'transcodingState'
  | 'indexUrl'
  | 'rewrapCompatibilityHash'
  | 'webProxyLocation'
  | 'editProxyLocation'
  | 'hasRelations'
  | 'webProxyAudioDownMixInfo'
  | 'postProcessSettings'
  | 'transcriptOffsetMs'
  | 'customIcons'
  | 'tamsReference'
  | 'thumbnailInfo'
  | CanceledStateFieldName
  > & {
  /**
   * This is here to be able to differentiate between a LoadedItem and Item consistently.
   * This is needed to avoid a massive refactor when introducing the first phase of incrementally
   * loading the item page.
   */
  loaded: boolean;

  // In memory state for metadata conflict resolution strategy.
  metadataSnapshot: MetadataSnapshot;

  audioChannels: number;
  name: string;  // TODO: name is not always defined and should be typed as optional

  // TODO: Unclear why it's NonNullable
  mediaSize: NonNullable<ExtendedItemDto['mediaSize']>;
  frameCaptureFrequency: Fraction;
  srtUrl: string;
  proxyAudioTrackUrls: string[],

  // TODO: These properties are being replaced by a class with the same name in ItemDetails.vue.
  // It is not possible to type this properly without introducing either a circular dependency, or a large
  // job to move the transcript code up to the common folder.
  // This also causes LoadedItem's to not be able to be stringified. These properties should not be stored on the item object.
  // Followup #2931
  timedTranscript: any; // TimedTranscript[];
  timedTranslatedTranscript: any; // TimedTranscript[];
  subtitleMarkers: any[]; // to be fixed by MIM-641
  paragraphMarkers: any[]; // to be fixed by MIM-641
  paragraphRulesParameters: any[];
  subtitleRulesParameters: any[];
  translationSubtitleMarkers: any[]; // to be fixed by MIM-641
  translationParagraphMarkers: any[]; // to be fixed by MIM-641
  translationParagraphRulesParameters: any[];
  translationSubtitleRulesParameters: any[];
  transcript: string;
  translatedTranscript: string;
  translationLanguage: string;
  translationVttUrl: string;
  translationSrtUrl: string;
  isTranslationUpdated: boolean;

  timedTranscriptUrl?: string;
  timedTranslatedTranscriptUrl?: string;

  celebrityDetectionEnabled?: boolean;
  personDetectionEnabled?: boolean;

  // TODO: Unclear why it's NonNullable
  mediaType: NonNullable<ExtendedItemDto['mediaType']>;

  titleHighlighted: string; // These are actually HTML
  descriptionHighlighted: string; // These are actually HTML
  searchHitsInCelebrities: number;
  searchHitsInPersons: number;
  searchHitsInTranscript: number;
  searchHitsInTranslatedTranscript: number;
  searchHitsInLabels: number;

  // How many matches in total did we get on exif tags
  searchHitsInExifTags: number;
  // The unique strings we matched. For instance, if we search
  // for "foo", we could get 2 matches for "FOO" and 1 for "Foo", which would
  // give searchHitsInExifTags=3 and exifTagsUniqueMatches=["FOO", "Foo"]
  exifTagsUniqueMatches?: string[];
  exifTagsUrl?: string;

  personId?: string;
  replacedBy?: string; // id of the item that this was replaced by
  replacementOf?: string; // id of old item version

  sequenceDetails?: any;

  editProxyIngested?: boolean;

  hasRelations: boolean;
  hasArchiveRelation: boolean;

  isP2Part?: boolean;

  createdBy?: string;

  relationsEntity?: RelationsEntity;
};

const itemUpdateRequestBaseProperties = <const>[
  // Filtered/sanitized
  'progressInfo',
  'uploadProgressInfo',
  'metadata',
  'transcriptionEnabled',
  'itemState',
  'isExternalUpload',
  'itemType',
  'detectedPersons',
  'detectedCelebrities',
  'detectedLabels',
  'visibleTo',
  'updatableBy',
  'media',
  'focusPoint',
  'frameCount',
  'timecode',
  'createdBy',

  // Directly reflected, unverified passthrough
  // TODO: Get rid of unverified passthrough
  'uploadMethods',
  'languageCode',
  'transcriptionService',
  'labelDetectionEnabled',
  'textDetectionEnabled',
  'personDetectionEnabled',
  'createUnknownPersons',
  'transcriptionChannels',
  'transcriptionChannelIdentification',
  'mediaType',
  'originalFileName',
  'mediaSize',
  'editProxyIngested',
  'technicalMetadata',
  'transcodingJobCanceled',
  'transcriptionJobCanceled',
  'personDetectionJobCanceled',
  'celebrityDetectionJobCanceled',
  'labelDetectionJobCanceled',
  'textDetectionJobCanceled',

  'keldaIngest',

  // Update-only, unverified passthrough
  // TODO: Get rid of unverified passthrough
  'sourceFileLocation',
  'celebrityDetectionEnabled',
  'indexLocation',

  // Backed by S3
  'sequenceDetails',
  'geoLocation',

  'liveRecording',

  'archiveFileTransferErrorMessage',
  'archiveFileTransferState',
  'archiveS3Bucket',
  'archiveLocationFullPath',
  'archivedOn',
  'isArchived',

  'conformDetailsIfRenderedSequence',
  'textDetectionSettings',
  'analyticPlugins',
  'editProxyFormat',
  'pagedProxy',
  'mediaFrameRate',
  'webProxyAudioDownMixInfo',
  'postProcessSettings',
  'reprocessBy',
  'rewrapCompatibilityHash',
  'transcodingState',
  'customIcons',
  'externalId',
];

type ItemUpdateRequestBase = Partial<Pick<ExtendedItemDto, typeof itemUpdateRequestBaseProperties[number]>>;

/*
 * These properties and the interface below represent the properties that we support
 * in the update item api payload which don't directly reflect properties on 'Item' itself
 * but we derive/compute the necessary stuff from them.
 */
const itemUpdateRequestSpecificProperties = <const>[
  'executeSameActionOnArchiveCopy',
  'warnings',
  'aafLocation',
  'folderParents',
  'supportTenantId',
  'metadataDelta',
  'upstreamItemId',
  'thumbnailInfo',
  'highresDeletion',
  'editProxyLocation',
  'webProxyLocation',
  'ingest',
  'ingestSourceFullPath',
  'ingestSourceStorageId',
  'ingestSourceS3Bucket',
  'externalProxyInfo',
  'reprocessActionType',
  'livePreviewLocation',
  'pendingArchiveRequest',
  'ingestSkipProcessing',
  'frameCaptureFrequency',
  'medialiveFrameCaptureFrequency',
  'generatedContentLocation',
  'enabledStreams',
  'highresFileDeleted',
];

interface ItemUpdateRequestSpecificPropertiesType {
  /**
   * This is a update command directive, not an item property, and therefore
   * does not in ExtendedItemDto.
   */
  executeSameActionOnArchiveCopy?: boolean;

  /**
   * The "warnings" property is a case in which the update API expects a
   * different structure shape than what is defined in ExtendedItemDto.
   *
   * TODO: Issue #1842
   */
  warnings?: { warnings: string[] };

  /**
   * For this property, some of the values are derived in the backend. As a
   * result, the update payload is a subset of what is actually stored in DB
   */
  aafLocation?: AafLocationUpdatePayload;

  folderParents?: string[] | null;

  supportTenantId?: string;

  /**
   * Update request DTO property only. This allows partial update of item metadata.
   */
  metadataDelta?: Partial<MetadataDeltaPayload>;
  upstreamItemId?: string; // used to add ChangeNotification relation

  thumbnailInfo?: ThumbnailInfo;

  /**
   * Update request DTO property only. This allows partial update of item highresDeletion.
   */
  highresDeletion?: HighresDeletionUpdateDto;

  /**
   * Edit proxy location references. Will be removed if null.
   */
  editProxyLocation?: ProxyFileLocation | null;
  /**
   * Web proxy location references. Will be removed if null.
   */
  webProxyLocation?: ProxyFileLocation | null;
  /**
   * Highres location reference. Will be removed if null.
   */
  ingest?: Ingest | null;

  /**
   * Highres location path for external cloud storage ingested items. Will be removed if null.
   */
  ingestSourceFullPath?: string | null;
  /**
   * ID of external cloud storage. Will be removed if null.
   */
  ingestSourceStorageId?: string | null;
  /**
   * External cloud storage name. Will be removed if null.
   */
  ingestSourceS3Bucket?: string | null;

  /**
   * Proxy reference for items ingested via a 'proxy-only' ingest config. Will be removed if null.
   */
  externalProxyInfo?: ExternalProxyDto | null;

  reprocessActionType?: ReprocessActionType;

  livePreviewLocation?: LivePreviewLocation | null;

  pendingArchiveRequest?: UpdatePendingArchiveRequest | null;

  ingestSkipProcessing?: boolean;

  frameCaptureFrequency?: Fraction | null;
  medialiveFrameCaptureFrequency?: Fraction | null;

  enabledStreams?: StreamRoleId[];

  generatedContentLocation?: GeneratedContentLocation | null;

  highresFileDeleted?: {type: 'kelda_on_premise', keldaId: string};
}

export type ItemUpdateRequestDto = ItemUpdateRequestBase & Pick<ItemUpdateRequestSpecificPropertiesType, typeof itemUpdateRequestSpecificProperties[number]>;

const itemUpdateRequestDtoProperties = <const>[...itemUpdateRequestBaseProperties, ...itemUpdateRequestSpecificProperties];

export interface ItemUpdateRequestWithLegacy extends ItemUpdateRequestDto {
  // Fields that the server doesn't really want

  id?: string;
  createdOn?: string;
}

// These are the whitelist properties that we want to allow in the update item api payload
export const itemUpdateRequestPropertiesWithLegacy = <const>['id', 'createdOn', ...itemUpdateRequestDtoProperties];



/**
 * Simplistic typeguard checking if a server answer is an item.
 * For now we assume if it's an object with properties 'id' & 'itemType' in it that it is a LoadedItem.
 */
export const isItem = (item: unknown): item is Item => {
  if (typeof item !== 'object') return false;
  if (!item) return false;
  return 'itemType' in item && ('id' in item || 'itemId' in item); // 'itemId' should be refactored. This is a bandaid fix - see MIM-721
};

export function validateGeneratedContentLocation(obj: unknown): string | CompleteType<GeneratedContentLocation> {
  if (!isObject(obj)) return 'generatedContentLocation must be an object';

  const probableLocation = obj as Partial<GeneratedContentLocation>;
  if (probableLocation.generatedMediaLocationId && typeof probableLocation.generatedMediaLocationId !== 'string') {
    return 'generatedContentLocation.generatedMediaLocationId must be a string';
  }

  if (probableLocation.thumbnail && !isInstanceOfFileLocation(probableLocation.thumbnail)) {
    return 'generatedContentLocation.thumbnail must be a FileLocation';
  }

  if (probableLocation.contentPrefix && typeof probableLocation.contentPrefix !== 'string') {
    return 'generatedContentLocation.contentPrefix must be a string';
  }

  return {
    generatedMediaLocationId: probableLocation.generatedMediaLocationId,
    thumbnail: probableLocation.thumbnail,
    contentPrefix: probableLocation.contentPrefix,
  };
}
